import { useEffect, useRef } from 'react';
import { SwiperRef } from 'swiper/react';
import { Carousel, CarouselSlide } from '../../../components/Carousel';
import ResponsiveImage from '../../../components/ResponsiveImage';
import { useTranslations } from 'next-intl';

interface IImage {
  src: string;
  alt?: string | null;
  fetchPriority?: 'high' | 'low' | undefined;
}
interface ImageViewerHeroVisualProps {
  src: string;
  alt?: string;
  priority?: boolean;
  fetchPriority?: 'high' | 'low' | undefined;
}
interface ImageViewerHeroProps {
  images: IImage[];
  selectedSlide: number;
  disabledMessage?: string;
  hasZoom?: boolean;
  onZoom?: (index: number) => void;
  onSlideChange?: (index: number) => void;
  children: React.ReactNode;
}

const ImageViewerHeroImage = ({
  src,
  alt = '',
  priority = false,
  fetchPriority,
}: ImageViewerHeroVisualProps) => {
  return (
    <ResponsiveImage
      width={517}
      height={517}
      objectFit="contain"
      src={src}
      alt={alt}
      fetchPriority={priority ? 'high' : fetchPriority}
      priority={priority}
    />
  );
};

const ImageViewerHero = ({
  children,
  images,
  selectedSlide = 0,
  disabledMessage,
  hasZoom = false,
  onZoom,
  onSlideChange,
}: ImageViewerHeroProps) => {
  const swiperRef = useRef<SwiperRef>(null);
  const tDesignSystem = useTranslations('DesignSystem');
  const hasMultipleImage = images.length > 1;

  useEffect(() => {
    if (
      swiperRef.current?.swiper &&
      selectedSlide !== swiperRef.current.swiper.activeIndex
    ) {
      swiperRef.current.swiper.slideTo(selectedSlide);
    }
  }, [selectedSlide]);

  return (
    <div
      className={`tw-relative tw-mx-auto tw-max-w-[517px] tw-self-start tw-overflow-hidden
      desktop:tw-mx-0 desktop:tw-rounded-lg desktop:tw-border
      desktop:tw-border-solid desktop:tw-border-blue-200
      [&>span]:!tw-block [&_.swiper-pagination]:tw-z-20 min-[960px]:[&_.swiper-pagination]:tw-hidden [&_.swiper-slide>span]:!tw-block`}
    >
      {!hasMultipleImage ? (
        hasZoom ? (
          <button
            type="button"
            aria-label={tDesignSystem('zoomButton', { name: images[0].alt })}
            className="tw-lf-button-clear"
            onClick={() => onZoom?.(0)}
          >
            <ImageViewerHeroImage
              src={images[0].src}
              alt={images[0].alt || ''}
              priority={true}
            />
          </button>
        ) : (
          <ImageViewerHeroImage
            src={images[0].src}
            alt={images[0].alt || ''}
            priority={true}
            fetchPriority={images[0].fetchPriority}
          />
        )
      ) : null}

      {hasMultipleImage && (
        <Carousel
          ref={swiperRef}
          initialSlide={0}
          slidesPerView={1}
          freeMode
          pagination
          onSlideChange={({ activeIndex }) => {
            if (activeIndex !== selectedSlide) {
              onSlideChange?.(activeIndex);
            }
          }}
        >
          {images.map((image, index) => {
            return (
              <CarouselSlide key={index}>
                {hasZoom ? (
                  <button
                    type="button"
                    aria-label={tDesignSystem('zoomButton', {
                      name: image.alt,
                    })}
                    className="tw-lf-button-clear"
                    onClick={() => onZoom?.(index)}
                  >
                    <ImageViewerHeroImage
                      src={image.src}
                      alt={image.alt || ''}
                      priority={index === 0}
                      fetchPriority={image.fetchPriority}
                    />
                  </button>
                ) : (
                  <ImageViewerHeroImage
                    src={image.src}
                    alt={image.alt || ''}
                    priority={index === 0}
                    fetchPriority={image.fetchPriority}
                  />
                )}
              </CarouselSlide>
            );
          })}
        </Carousel>
      )}

      {children}

      {disabledMessage && (
        <div
          className={`tw-pointer-events-none tw-absolute tw-inset-0
          tw-z-10 tw-flex tw-items-center
          tw-justify-center tw-bg-[rgba(255,255,255,.5)]`}
        >
          <p className={`tw-lf-subtitle-md tw-bg-white tw-px-1`}>
            {disabledMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageViewerHero;
